/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 *
 * POC COPY — mirrors the subset of the Chat API + LRR resolver used by the
 * dashboard drawer. Sources of truth:
 *   cds-ai-service/frontend/src/api/queries/chat.ts
 *   cds-ai-service/frontend/src/api/http/http.ts
 *   cds-ai-service/frontend/src/api/http/apiConfig.ts
 *   cds-ai-service/frontend/src/api/resolve-inline-or-lrr-text.js
 *   cds-ai-service/frontend/src/api/pollLrr.ts
 *   cds-ai-service/frontend/src/utils/lrr.js
 *   cds-ai-service/backend/v4/agents/runs/service.js (canonical toRunResponse)
 *   ./readAgentRunResult.js
 */

import { http } from '../../http/http';
import {
  applyVerboseRunFailure,
  getLrrFailureMessage,
  pickAgentRunPayload,
  readAgentRunResult,
} from './readAgentRunResult';

export { getLrrFailureMessage };

export const REQUEST_ABORTED_ERROR = 'REQUEST_ABORTED';

export const isRequestAborted = (error, signal) => signal?.aborted === true || error?.message === REQUEST_ABORTED_ERROR;

const throwIfAborted = signal => {
  if (signal?.aborted === true) {
    throw new Error(REQUEST_ABORTED_ERROR);
  }
};

const AI_API_NAME = 'ai';
const AI_HTTP_OPTIONS = { suppressUnauthenticatedOn401: true };
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '[::1]']);

const isLocalDevHostname = hostname => LOCAL_HOSTNAMES.has(hostname);

export const isCommandIntelligenceLocalDev = () => {
  const { hostname } = typeof window !== 'undefined' ? window.location : { hostname: '' };

  return isLocalDevHostname(hostname);
};

const resolveRunForChat = (runPayload, { verboseErrors }) => {
  const { responseText, metadata } = readAgentRunResult(runPayload);

  return applyVerboseRunFailure({ runPayload, responseText, metadata }, { verboseErrors });
};

const DEFAULT_POLL_INTERVAL_MS = 1500;
const DEFAULT_POLL_MAX_ATTEMPTS = 120;
const TERMINAL_LRR_STATUSES = ['completed', 'success', 'finished', 'failed', 'error', 'cancelled'];
const FAILED_LRR_STATUSES = ['failed', 'error', 'cancelled'];

const isTerminalLrrStatus = status => {
  if (typeof status !== 'string' || status.trim() === '') {
    return false;
  }

  return TERMINAL_LRR_STATUSES.includes(status.trim().toLowerCase());
};

export const isFailedLrrStatus = status => {
  if (typeof status !== 'string' || status.trim() === '') {
    return false;
  }

  return FAILED_LRR_STATUSES.includes(status.trim().toLowerCase());
};

const getLrrPollCrn = res => {
  if (res === null || res === undefined || typeof res !== 'object') {
    return '';
  }

  const { lrrCrn } = res;

  if (typeof lrrCrn === 'string' && lrrCrn.trim() !== '') {
    return lrrCrn;
  }

  const { crn } = res;

  if (typeof crn === 'string' && crn.trim() !== '') {
    return crn;
  }

  return '';
};

const needsLrrPoll = res =>
  res !== null &&
  res !== undefined &&
  typeof res === 'object' &&
  typeof res.resultUrl === 'string' &&
  getLrrPollCrn(res) !== '';

export const createChatChannel = (entityRef, data, { signal } = {}) =>
  http.post({
    apiName: AI_API_NAME,
    path: `/${entityRef}/chat-channels`,
    data,
    signal,
    ...AI_HTTP_OPTIONS,
  });

/**
 * Monthly AI credit usage vs limits (profile + entity) from cds-ai-service.
 * @param {string} entityRef
 * @returns {Promise<{
 *   monthKey: string,
 *   conversion: { creditsPerUsd: number },
 *   entity: { monthlyCreditLimit: number | null, creditsUsed: number, percentUsed: number | null },
 *   profile: { profileRef: string, monthlyCreditLimit: number | null, creditsUsed: number, percentUsed: number | null } | null
 * } | null>}
 */
export const getAiLimitUsageSummary = entityRef =>
  http.get({
    apiName: AI_API_NAME,
    path: `/${entityRef}/ai-limits/usage-summary`,
    ...AI_HTTP_OPTIONS,
  });

export const submitFeedback = (entityRef, data) =>
  http.post({
    apiName: AI_API_NAME,
    path: `/${entityRef}/chat/feedback`,
    data,
    ...AI_HTTP_OPTIONS,
  });

export const streamChatResponse = (entityRef, chatChannelRef, data, { signal } = {}) => {
  const body = {
    text: data.text,
    role: data.role || 'user',
    agentRef: data.agentRef,
    foundationModel: data.foundationModel,
  };

  if (data.attachedContext !== null && data.attachedContext !== undefined && typeof data.attachedContext === 'object') {
    body.attachedContext = data.attachedContext;
  }

  return http.post({
    apiName: AI_API_NAME,
    path: `/${entityRef}/chat-channels/${chatChannelRef}/stream`,
    data: body,
    signal,
    ...AI_HTTP_OPTIONS,
  });
};

export const getLrr = (entityRef, lrrRef) =>
  http.get({
    apiName: AI_API_NAME,
    path: `/${entityRef}/lrrs/${lrrRef}`,
    ...AI_HTTP_OPTIONS,
  });

export const createChatCompletion = (entityRef, { message, model = 'nova-micro-us', systemPrompt, options = {} }) => {
  const context = {};

  if (typeof systemPrompt === 'string' && systemPrompt.trim() !== '') {
    context.systemPrompt = systemPrompt.trim();
  }

  const data = {
    message,
    model,
    providerType: 'Bedrock',
    options,
  };

  if (Object.keys(context).length > 0) {
    data.context = context;
  }

  return http.post({ apiName: AI_API_NAME, path: `/${entityRef}/chat/completions`, data });
};

const delay = (ms, signal) => {
  if (signal?.aborted) {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    const id = setTimeout(resolve, ms);

    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(id);
          resolve();
        },
        { once: true }
      );
    }
  });
};

const pollLrr = async (
  entityRef,
  crn,
  { intervalMs = DEFAULT_POLL_INTERVAL_MS, maxAttempts = DEFAULT_POLL_MAX_ATTEMPTS, signal, onPoll } = {}
) => {
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      return null;
    }

    const lrr = await getLrr(entityRef, crn);

    if (!lrr) {
      return null;
    }

    if (typeof onPoll === 'function') {
      await onPoll(lrr);
    }

    if (isTerminalLrrStatus(lrr.status)) {
      return lrr;
    }

    await delay(intervalMs, signal);
  }

  return null;
};

export const resolveInlineOrLrrResponse = async ({ entityRef, response, signal, pollOptions = {} }) => {
  throwIfAborted(signal);

  const verboseErrors = isCommandIntelligenceLocalDev();
  const inlineRun = pickAgentRunPayload(response);
  const inline = resolveRunForChat(inlineRun, { verboseErrors });

  if (!entityRef || !needsLrrPoll(response)) {
    throwIfAborted(signal);

    return {
      responseText: inline.responseText,
      metadata: inline.metadata,
      isRunError: inline.isRunError,
      response,
    };
  }

  const lrrCrn = getLrrPollCrn(response);

  if (!lrrCrn) {
    throwIfAborted(signal);

    const fallbackRun = pickAgentRunPayload({ result: response?.result });
    const fallback = resolveRunForChat(fallbackRun, { verboseErrors });

    return {
      responseText: fallback.responseText,
      metadata: fallback.metadata,
      isRunError: fallback.isRunError,
      response,
    };
  }

  const lrr = await pollLrr(entityRef, lrrCrn, {
    signal,
    ...pollOptions,
  });

  if (signal?.aborted === true) {
    throw new Error(REQUEST_ABORTED_ERROR);
  }

  if (lrr === null || lrr === undefined) {
    throw new Error('Request timed out or was cancelled.');
  }

  if (isFailedLrrStatus(lrr?.status)) {
    throw new Error(getLrrFailureMessage(lrr));
  }

  const terminalRun = pickAgentRunPayload({ result: lrr.result });
  const terminal = resolveRunForChat(terminalRun, { verboseErrors });

  return {
    responseText: terminal.responseText,
    metadata: terminal.metadata,
    isRunError: terminal.isRunError,
    lrr,
  };
};

export const getFriendlyErrorMessage = (error, userMessage = '') => {
  const errorString = error?.toString() || error || '';

  if (errorString.includes('500') || errorString.includes('Internal Server Error')) {
    return "I'm having trouble processing your request right now. Please try again in a moment.";
  }

  if (errorString.includes('404') || errorString.includes('Not Found')) {
    return "I couldn't find the information you're looking for. Could you try rephrasing your question?";
  }

  if (errorString.includes('timeout') || errorString.includes('Timeout')) {
    return 'Your request is taking longer than expected. Please try again.';
  }

  if (errorString.includes('401') || errorString.includes('Unauthorized')) {
    return 'I need to verify your access. Please refresh the page and try again.';
  }

  if (errorString.includes('403') || errorString.includes('Forbidden')) {
    return "I don't have permission to access that information. Please contact your administrator.";
  }

  if (errorString.includes('429') || errorString.includes('Too Many Requests')) {
    return "I'm getting a lot of requests right now. Please wait a moment and try again.";
  }

  if (errorString.includes('network') || errorString.includes('Network')) {
    return "I'm having trouble connecting. Please check your internet connection and try again.";
  }

  if (typeof userMessage === 'string' && userMessage.trim() !== '') {
    return 'Something went wrong while answering that. Please try again or ask me something else.';
  }

  return 'Something went wrong while processing your request. Please try again or ask me something else.';
};

const isFetchResponse = error =>
  error !== null && error !== undefined && typeof error === 'object' && 'status' in error;

const parseResponseBodyMessage = async response => {
  if (!isFetchResponse(response) || typeof response?.json !== 'function') {
    return '';
  }

  try {
    const body = await response.clone().json();
    const message = body?.message ?? body?.error ?? body?.detail;

    return typeof message === 'string' ? message.trim() : '';
  } catch {
    return '';
  }
};

const isInvokePermissionMessage = message => {
  const normalized = typeof message === 'string' ? message.toLowerCase() : '';

  return (
    normalized.includes('insufficient permissions to invoke') ||
    normalized.includes('invoke-agent') ||
    normalized.includes('not authorized to invoke') ||
    normalized === 'not_authorized'
  );
};

/**
 * Resolve a permission-denial message for AI access probes (usage-summary gate, etc.).
 * Returns null when the error is not a permission denial (e.g. session expiry, network).
 * @param {unknown} error
 * @param {import('react-intl').IntlShape} intl
 * @param {{ treat401AsAccessDenied?: boolean }} [options]
 * @returns {Promise<string | null>}
 */
export const resolveAiAccessDeniedMessage = async (error, intl, options = {}) => {
  const { treat401AsAccessDenied = false } = options;
  const response = isFetchResponse(error) ? error : null;

  if (response?.status === 403) {
    const apiMessage = await parseResponseBodyMessage(response);

    if (apiMessage !== '') {
      return apiMessage;
    }

    return intl.formatMessage({ id: 'command_intelligence_error_missing_invoke_permission' });
  }

  if (response?.status === 401) {
    const apiMessage = await parseResponseBodyMessage(response);

    if (treat401AsAccessDenied || isInvokePermissionMessage(apiMessage)) {
      return intl.formatMessage({ id: 'command_intelligence_error_missing_invoke_permission' });
    }
  }

  return null;
};

/**
 * Resolve a user-visible chat API error message (handles fetch Response objects).
 * @param {unknown} error
 * @param {import('react-intl').IntlShape} intl
 * @param {string} [userMessage]
 * @returns {Promise<string>}
 */
export const resolveChatApiErrorMessage = async (error, intl, userMessage = '') => {
  const accessDeniedMessage = await resolveAiAccessDeniedMessage(error, intl);

  if (accessDeniedMessage !== null) {
    return accessDeniedMessage;
  }

  const response = isFetchResponse(error) ? error : null;

  if (response?.status === 401) {
    return intl.formatMessage({ id: 'command_intelligence_error_session_expired' });
  }

  if (error === null) {
    return intl.formatMessage({ id: 'command_intelligence_error_session_expired' });
  }

  return getFriendlyErrorMessage(
    error instanceof Error ? error : new Error(String(response?.status ?? error)),
    userMessage
  );
};
